import AgoraRTM from 'agora-rtm-sdk'
import { useContext, useEffect, useRef, useState } from 'react'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { getAgoraSignalingToken } from 'src/consumer/services/featureServices'

const appId = import.meta.env.VITE_AGORA_APP_ID

export default function AgoraSignallingComponent({ 
  deviceId, 
  dispatchEventKey,
  onConnectionStatus,
  messageFilter,
  enableLogging = true
}) {
  const { profileData } = useContext(ProfileContext)

  const [rtmClient, setRtmClient] = useState(null)
  const [connectionStatus, setConnectionStatus] = useState('disconnected')

  const clientRef = useRef(null)
  const loginInProgressRef = useRef(false)
  const isMountedRef = useRef(true)
  const previousDeviceIdRef = useRef(null)

  // Helper function for conditional logging
  const log = (message, ...args) => {
    if (enableLogging) {
      console.log(message, ...args)
    }
  }

  const logInfo = (message, ...args) => {
    if (enableLogging) {
      console.info(message, ...args)
    }
  }

  const logWarn = (message, ...args) => {
    if (enableLogging) {
      console.warn(message, ...args)
    }
  }

  const logError = (message, ...args) => {
    if (enableLogging) {
      console.error(message, ...args)
    }
  }

  // Update connection status and notify parent
  const updateConnectionStatus = (status) => {
    setConnectionStatus(status)
    if (onConnectionStatus) {
      onConnectionStatus(status)
    }
  }

  async function initializeRTMClient() {
    const shouldInitialize = profileData && deviceId
    
    console.log('[AGORA RTM] initializeRTMClient called', {
      hasProfileData: !!profileData,
      deviceId,
      previousDeviceId: previousDeviceIdRef.current,
      hasExistingClient: !!clientRef.current,
      shouldInitialize,
      timestamp: new Date().toISOString()
    })
    
    if (!shouldInitialize) {
      console.warn('[AGORA RTM] Not initializing - missing profileData or deviceId', {
        hasProfileData: !!profileData,
        deviceId,
        timestamp: new Date().toISOString()
      })
      previousDeviceIdRef.current = null
      return
    }

    // Only reinitialize if deviceId has actually changed
    if (previousDeviceIdRef.current === deviceId && clientRef.current) {
      log('RTM client already initialized with same deviceId, skipping reinitialization')
      return
    }

    // Clean up any existing client before creating a new one
    // This prevents "kicked off by remote session" errors from stale connections
    if (clientRef.current) {
      try {
        const connectionState = clientRef.current.getConnectionState?.()
        console.warn('[AGORA RTM] DISCONNECTING - Cleaning up existing RTM client before reinitializing', {
          reason: 'deviceId or profileData changed',
          previousDeviceId: previousDeviceIdRef.current,
          newDeviceId: deviceId,
          connectionState,
          timestamp: new Date().toISOString()
        })
        
        logInfo('Cleaning up existing RTM client before reinitializing (deviceId changed)')
        // Cancel any pending login
        loginInProgressRef.current = false
        
        // Check connection state before attempting logout
        if (connectionState && connectionState !== 'DISCONNECTED' && connectionState !== 'ABORTED') {
          try {
            await clientRef.current.logout().catch((err) => {
              // Ignore WebSocket state errors during cleanup
              if (err?.message?.includes('CLOSING') || err?.message?.includes('CLOSED')) {
                logInfo('RTM: WebSocket already closing/closed during cleanup (expected)')
              } else {
                logWarn('RTM: Error during logout cleanup:', err)
              }
            })
            console.log('[AGORA RTM] Successfully logged out during cleanup')
          } catch (logoutError) {
            // Ignore errors during cleanup - WebSocket may already be closed
            if (!logoutError?.message?.includes('CLOSING') && !logoutError?.message?.includes('CLOSED')) {
              logWarn('RTM: Error during logout:', logoutError)
            }
          }
        }
        
        clientRef.current = null
        setRtmClient(null)
      } catch (error) {
        logError('Error cleaning up RTM client:', error)
        // Ensure refs are cleared even if cleanup fails
        clientRef.current = null
        setRtmClient(null)
      }
    }

    if (!clientRef.current) {
      log('Initializing RTM client with ID:', deviceId)
      updateConnectionStatus('initializing')
      // Create RTM client
      // Note: Agora RTM SDK 2.x uses new AgoraRTM.RTM(appId, userId) constructor
      const rtmClient = await new AgoraRTM.RTM(appId, deviceId)
      
      log('RTM client created:', rtmClient)
      setRtmClient(rtmClient)
      clientRef.current = rtmClient
      previousDeviceIdRef.current = deviceId

      // if(getAumhumScreenId()){
      //   const aumhumScreenRtmClient = await new AgoraRTM.RTM(appId, getAumhumScreenId())
      // }
    }

    const client = clientRef.current
    log('RTM client initialized:', client)
  }

  useEffect(() => {
    isMountedRef.current = true
    initializeRTMClient()
    
    return () => {
      isMountedRef.current = false
      loginInProgressRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData, deviceId])

  useEffect(() => {
    if (!rtmClient) return
    const messageHandler = (event) => {
      if (!event?.message) {
        logWarn('RTM: Received message event without message property')
        return
      }
      try {
        const messageObj = JSON.parse(event?.message)
        const command = messageObj?.command
        if (!command) {
          logWarn('RTM: Received message without command:', messageObj)
          return
        }
        
        // Apply message filter if provided
        if (messageFilter && !messageFilter(messageObj)) {
          log('Message filtered out:', messageObj)
          return
        }
        
        log('COMMAND FROM SIGNALING: ' + command)
        logInfo('RTM: Dispatching signaling event', {
          command,
          dispatchEventKey,
          deviceId,
          fullMessage: messageObj,
          timestamp: new Date().toISOString()
        })
        
        // Enhanced logging for ASK_NEXT_ACTION_NOW command
        if (command === 'ask-next-action-now' || command === 'mentor-command.ask-next-action-now') {
          console.log('[AGORA RTM] ASK_NEXT_ACTION_NOW signal received!', {
            command,
            deviceId,
            fullMessage: messageObj,
            dispatchEventKey,
            timestamp: new Date().toISOString()
          })
        }
        
        const signalingEvent = new CustomEvent(dispatchEventKey, {
          detail: { command: command, ...messageObj },
        })
        window.dispatchEvent(signalingEvent)
        
        log('RTM: Signaling event dispatched successfully')
      } catch (error) {
        logError('Failed to parse message:', error)
        logError('Raw message:', event?.message)
      }
    }

    async function initRTM() {
      const client = clientRef.current

      // Prevent multiple simultaneous login attempts
      if (loginInProgressRef.current) {
        logWarn('RTM: Login already in progress, skipping duplicate attempt')
        return
      }

      // Check if already logged in
      if (client && client.getConnectionState && client.getConnectionState() === 'CONNECTED') {
        log('RTM: Already connected, skipping login')
        updateConnectionStatus('connected')
        return
      }

      logInfo('Logging in to Agora RTM with ID:', deviceId)
      logInfo('Logging in to Agora RTM with RTM Client:', client)
      
      loginInProgressRef.current = true
      
      try {
        updateConnectionStatus('connecting')
        
        // Validate deviceId before requesting token
        if (!deviceId) {
          logError('RTM: deviceId is required but was not provided')
          updateConnectionStatus('failed')
          loginInProgressRef.current = false
          return
        }

        // Check if component is still mounted before proceeding
        if (!isMountedRef.current) {
          logWarn('RTM: Component unmounted, canceling login')
          loginInProgressRef.current = false
          return
        }

        const response = await getAgoraSignalingToken(deviceId)
        
        // Check if component is still mounted
        if (!isMountedRef.current) {
          logWarn('RTM: Component unmounted during token fetch, canceling login')
          loginInProgressRef.current = false
          return
        }

        // Check if response is an error object
        if (response?.isAxiosError || response?.response) {
          logError('RTM: Failed to get token from server:', response?.response?.data || response?.message)
          updateConnectionStatus('failed')
          loginInProgressRef.current = false
          return
        }

        if (response?.status === 200) {
          const token = response.data
          if (!token || typeof token !== 'string' || token.trim() === '') {
            logError('RTM: Invalid token received from server (empty or invalid format)')
            updateConnectionStatus('failed')
            loginInProgressRef.current = false
            return
          }

          // Check if component is still mounted and login is still needed
          if (!isMountedRef.current || !loginInProgressRef.current) {
            logWarn('RTM: Login canceled (component unmounted or new login started)')
            return
          }

          log('Logging in to RTM for client ', client)
          try {
            await client.login({ token })
            log('RTM Login successful')
            if (isMountedRef.current) {
              updateConnectionStatus('connected')
            }
          } catch (loginError) {
            // Handle login cancellation gracefully
            if (loginError?.code === -10023) {
              logWarn('RTM: Login was canceled (likely due to new login attempt or component re-initialization)')
              // This is expected behavior, not a real error
              return
            }
            // Handle referrer/domain restriction errors gracefully
            if (loginError?.code === 17 || 
                loginError?.message?.includes('referrerNotAllowedMapError') ||
                loginError?.message?.includes('referrer') ||
                loginError?.message?.includes('domain')) {
              logWarn('RTM: Domain/referrer restriction error - this is a configuration issue, not a code error')
              updateConnectionStatus('failed')
              loginInProgressRef.current = false
              return
            }
            throw loginError // Re-throw other errors
          }

          client.addEventListener('message', messageHandler)

          // Handle connection state changes (including being kicked off)
          client.on('ConnectionStateChanged', (newState, reason) => {
            // Only process if component is still mounted
            if (!isMountedRef.current) {
              return
            }
            
            logInfo('RTM Connection state changed:', { newState, reason })
            
            if (newState === 'DISCONNECTED' || newState === 'ABORTED') {
              console.error('[AGORA RTM] DISCONNECTED/ABORTED', {
                newState,
                reason,
                deviceId,
                timestamp: new Date().toISOString()
              })
              
              if (reason === 'REMOTE_LOGIN' || reason === 'KICKED_BY_REMOTE') {
                console.error('[AGORA RTM] KICKED OFF - Same deviceId connected from another location!', {
                  deviceId,
                  reason,
                  timestamp: new Date().toISOString()
                })
                logWarn('RTM: Kicked off by remote session. This usually means the same user connected from another device.')
                updateConnectionStatus('kicked_off')
                
                // Optionally attempt to reconnect after a delay
                // For now, just log the error - the user may need to refresh
                setTimeout(() => {
                  if (isMountedRef.current) {
                    logWarn('RTM: Attempting to reconnect after being kicked off...')
                    // The component will re-initialize if needed
                  }
                }, 2000)
              } else {
                console.error('[AGORA RTM] Connection lost (not kicked off)', {
                  newState,
                  reason,
                  deviceId,
                  timestamp: new Date().toISOString()
                })
                logWarn('RTM: Connection lost:', { newState, reason })
                updateConnectionStatus('disconnected')
              }
            } else if (newState === 'CONNECTING') {
              console.log('[AGORA RTM] Connecting...', { deviceId, timestamp: new Date().toISOString() })
              updateConnectionStatus('connecting')
            } else if (newState === 'CONNECTED') {
              console.log('[AGORA RTM] CONNECTED', { deviceId, timestamp: new Date().toISOString() })
              updateConnectionStatus('connected')
            }
          })
          
          // Handle database sync errors gracefully (error code 101)
          // This is an internal Agora error that can occur during connection setup
          client.on('error', (error) => {
            // Error code 101 is a database connection error - handle gracefully
            if (error?.code === 101 || error?.message?.includes('Database connection failed')) {
              logWarn('RTM: Database sync connection error (101) - this is usually transient and will retry automatically')
              // Don't update connection status - let the SDK handle retry
              return
            }
            // Log other errors
            logError('RTM: Connection error:', error)
          })

          client.addEventListener('tokenPrivilegeWillExpire', async () => {
            logWarn('RTM Token will expire soon. Fetching a new one...')
            updateConnectionStatus('refreshing')
            
            // Validate deviceId before requesting token
            if (!deviceId) {
              logError('RTM: Cannot refresh token - deviceId is missing')
              updateConnectionStatus('failed')
              return
            }

            const refreshResponse = await getAgoraSignalingToken(deviceId)
            
            // Check if response is an error object
            if (refreshResponse?.isAxiosError || refreshResponse?.response) {
              logError('RTM: Failed to refresh token from server:', refreshResponse?.response?.data || refreshResponse?.message)
              updateConnectionStatus('failed')
              return
            }

            if (refreshResponse?.status === 200) {
              const newToken = refreshResponse.data
              if (!newToken || typeof newToken !== 'string' || newToken.trim() === '') {
                logError('RTM: Invalid refresh token received from server (empty or invalid format)')
                updateConnectionStatus('failed')
                return
              }

              try {
                await client.renewToken(newToken)
                log('RTM Token renewed successfully')
                updateConnectionStatus('connected')
              } catch (renewError) {
                logError('RTM: Failed to renew token with Agora:', renewError)
                updateConnectionStatus('failed')
              }
            } else {
              logError('RTM: Failed to renew token - server returned status:', refreshResponse?.status)
              updateConnectionStatus('failed')
            }
          })
        } else {
          if (isMountedRef.current) {
            updateConnectionStatus('failed')
          }
        }
      } catch (error) {
        // Handle login cancellation gracefully
        if (error?.code === -10023) {
          logWarn('RTM: Login was canceled (likely due to new login attempt or component re-initialization)')
          // This is expected behavior, not a real error - don't update status
        } else if (error?.code === 17 || 
                   error?.message?.includes('referrerNotAllowedMapError') ||
                   error?.message?.includes('referrer') ||
                   error?.message?.includes('domain')) {
          // Handle referrer/domain restriction errors gracefully
          logWarn('RTM: Domain/referrer restriction error - this is a configuration issue, not a code error')
          if (isMountedRef.current) {
            updateConnectionStatus('failed')
          }
        } else {
          logError('Agora RTM login error:', error)
          if (isMountedRef.current) {
            updateConnectionStatus('failed')
          }
        }
      } finally {
        loginInProgressRef.current = false
      }
    }
    if (rtmClient) {
      initRTM()
    } else {
      logError('RTM client is not initialized or is null. Not logging in.')
    }
    return () => {
      console.warn('[AGORA RTM] DISCONNECTING - useEffect cleanup (component unmounting or rtmClient changed)', {
        hasRtmClient: !!rtmClient,
        hasClientRef: !!clientRef.current,
        isMounted: isMountedRef.current,
        timestamp: new Date().toISOString()
      })
      
      // Cancel any pending login
      loginInProgressRef.current = false
      
      if (rtmClient && clientRef.current) {
        const connectionState = clientRef.current.getConnectionState?.()
        console.warn('[AGORA RTM] Logging out RTM client', {
          connectionState,
          timestamp: new Date().toISOString()
        })
        
        updateConnectionStatus('disconnecting')
        
        // Remove event listeners
        try {
          clientRef.current.removeEventListener('message', messageHandler)
        } catch (err) {
          // Ignore errors removing listeners
        }
        
        // Check connection state before attempting logout
        if (connectionState && connectionState !== 'DISCONNECTED' && connectionState !== 'ABORTED') {
          clientRef.current.logout().then(() => {
            console.log('[AGORA RTM] Successfully logged out during cleanup')
            if (isMountedRef.current) {
              updateConnectionStatus('disconnected')
            }
          }).catch((err) => {
            // Ignore WebSocket state errors during cleanup
            if (err?.message?.includes('CLOSING') || err?.message?.includes('CLOSED')) {
              // Expected - WebSocket already closing/closed
            } else {
              logWarn('RTM: Error during cleanup logout:', err)
            }
            if (isMountedRef.current) {
              updateConnectionStatus('disconnected')
            }
          })
        } else {
          // Already disconnected
          console.log('[AGORA RTM] Already disconnected, skipping logout')
          if (isMountedRef.current) {
            updateConnectionStatus('disconnected')
          }
        }
      }
    }
    //not putting the screenDeviceConfigId in the dependency array as it is already being used in the initializeRTMClient function,
    //  meaning it will update the client when the screenDeviceConfigId changes
  }, [rtmClient])

  useEffect(() => {
    const handleUnload = () => {
      const client = clientRef.current
      if (client) {
        client.logout().catch(() => {}) // no need to await here
      }
    }

    window.addEventListener('beforeunload', handleUnload)
    return () => {
      window.removeEventListener('beforeunload', handleUnload)
    }
  }, [])

  return <></>
}
// export default function AgoraSignallingComponent({ deviceId, dispatchEventKey }) {
//   const { profileData } = useContext(ProfileContext)

//   let screenDeviceConfigId = null
//   const ROLE_CA_CONFIG_STRING = localStorage.getItem('ROLE_CA_CONFIG')
//   if (ROLE_CA_CONFIG_STRING) {
//     screenDeviceConfigId = JSON.parse(ROLE_CA_CONFIG_STRING)?.SCREEN_DEVICE_CONFIG_ID
//   }

//   const [rtmClient, setRtmClient] = useState(null)

//   const clientRef = useRef(null)

//   async function initializeRTMClient() {
//     const roles = getAllRolesInBoolean()
//     const shouldInitialize =
//       profileData && screenDeviceConfigId && (roles.isClinicAssistant || roles.isChamberScreen)
//     if (!shouldInitialize) return

//     if (!clientRef.current) {
//       console.log('Initializing RTM client with ID:', screenDeviceConfigId)
//       // await import('agora-rtm-sdk').then((AgoraRTM) => {
//       // })
//       const rtmClient = await new AgoraRTM.RTM(appId, screenDeviceConfigId)
//       console.log('RTM client created:', rtmClient)
//       setRtmClient(rtmClient)
//       clientRef.current = rtmClient

//       // if(getAumhumScreenId()){
//       //   const aumhumScreenRtmClient = await new AgoraRTM.RTM(appId, getAumhumScreenId())
//       // }
//     }

//     const client = clientRef.current
//     console.log('RTM client initialized:', client)
//   }

//   useEffect(() => {
//     initializeRTMClient()
//   }, [profileData, screenDeviceConfigId])

//   useEffect(() => {
//     if (!rtmClient) return
//     const messageHandler = (event) => {
//       if (!event?.message) {
//         return
//       }
//       try {
//         const messageObj = JSON.parse(event?.message)
//         const command = messageObj?.command
//         if (!command) {
//           return
//         }
//         console.log('COMMAND FROM SINGALING: ' + command)
//         const signalingEvent = new CustomEvent(DISPATCH_EVENT_KEYS.signalingEvent, {
//           detail: { command: command, ...messageObj },
//         })
//         window.dispatchEvent(signalingEvent)
//       } catch (error) {
//         console.error('Failed to parse message:', error)
//       }
//     }

//     async function initRTM() {
//       const client = clientRef.current

//       console.info('Logging in to Agora RTM with ID:', screenDeviceConfigId)
//       console.info('Logging in to Agora RTM with RTM Client:', client)
//       try {
//         const response = await getAgoraSignalingToken(screenDeviceConfigId)
//         if (response?.status === 200) {
//           const token = response.data
//           if (!token) return

//           console.log('Logging in to RTM for client ', client)
//           await client.login({ token })
//           console.log('RTM Login successful')

//           client.addEventListener('message', messageHandler)

//           client.addEventListener('tokenPrivilegeWillExpire', async () => {
//             console.warn('RTM Token will expire soon. Fetching a new one...')
//             const refreshResponse = await getAgoraSignalingToken(screenDeviceConfigId)
//             if (refreshResponse?.status === 200) {
//               const newToken = refreshResponse.data
//               await client.renewToken(newToken)
//               console.log('RTM Token renewed successfully')
//             } else {
//               console.error('Failed to renew RTM token')
//             }
//           })
//         }
//       } catch (error) {
//         console.error('Agora RTM login error:', error)
//       }
//     }
//     if (rtmClient) {
//       initRTM()
//     } else {
//       console.error('RTM client is not initialized or is null. Not logging in.')
//     }
//     return () => {
//       if (rtmClient) {
//         clientRef.current?.removeEventListener('message', messageHandler)
//         clientRef.current?.logout()
//       }
//     }
//     //not putting the screenDeviceConfigId in the dependency array as it is already being used in the initializeRTMClient function,
//     //  meaning it will update the client when the screenDeviceConfigId changes
//   }, [rtmClient])

//   useEffect(() => {
//     const handleUnload = () => {
//       const client = clientRef.current
//       if (client) {
//         client.logout().catch(() => {}) // no need to await here
//       }
//     }

//     window.addEventListener('beforeunload', handleUnload)
//     return () => {
//       window.removeEventListener('beforeunload', handleUnload)
//     }
//   }, [])

//   return <></>
// }
